import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WavyDivider } from '../components/WavyDivider';
import { BlogCard } from '../components/BlogCard';
import { SectionDivider, SURFACE } from '../components/blocks/SectionDivider';
import { blogPosts, categories, type ContentBlock, type BlogPost as BlogPostType } from '../data/blog';
import { useSeo } from '../lib/useSeo';
import { articleSchema } from '../lib/schema';

/**
 * PAGE: /journal/:slug
 * WORDPRESS: `single.php`
 *
 * Blocks: article_hero → article_body (flexible content) → related_posts
 *
 * The body renders from data/blog.ts `ContentBlock[]`, which maps directly onto
 * an ACF flexible-content field with paragraph / heading / list / quote layouts.
 */

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/** Flattens the structured body into plain text for `articleBody` in schema. */
function plainText(content: readonly ContentBlock[]): string {
  return content.map(block => {
    if (block.type === 'list') return block.items.join(' ');
    return block.text;
  }).join(' ');
}
function ContentRenderer({
  block
}: {
  block: ContentBlock;
}) {
  switch (block.type) {
    case 'heading':
      return <h2 className="mb-6 mt-12 font-serif text-3xl text-charcoal md:text-4xl" data-id="element-443" data-mp="1kvfctkzuk16q">
          {block.text}
        </h2>;
    case 'paragraph':
      return <p className="text-lg font-light leading-relaxed text-charcoal-50" data-id="element-444" data-mp="1b2qe7qpeahst">
          {block.text}
        </p>;
    case 'list':
      return block.ordered ? <ol className="list-decimal space-y-4 pl-6 marker:font-serif marker:text-sage-700" data-id="element-445" data-mp="23rdvlb70wn13">
          {block.items.map(item => <li key={item} className="pl-2 text-lg font-light leading-relaxed text-charcoal-50" data-id="element-446" data-mp="28k6v9knmx56b">
              {item}
            </li>)}
        </ol> : <ul className="space-y-3" data-id="element-447" data-mp="2dgaw5z27qdts">
          {block.items.map(item => <li key={item} className="flex gap-3 text-lg font-light leading-relaxed text-charcoal-50" data-id="element-448" data-mp="28f53g18jyqk9">
              <span className="select-none text-sage-700" aria-hidden="true" data-id="element-449" data-mp="1zfrst58ozvxy">
                –
              </span>
              <span data-id="element-450" data-mp="1upbcnddhlw7w">{item}</span>
            </li>)}
        </ul>;
    case 'quote':
      return <blockquote className="my-10 border-l-2 border-sage pl-6" data-id="element-451" data-mp="1pp5ldyb4qbqc">
          <p className="mb-3 font-serif text-2xl italic leading-snug text-charcoal md:text-3xl" data-id="element-452" data-mp="1kqvp1kdo5535">
            {block.text}
          </p>
          {block.author && <cite className="text-sm tracking-widest text-charcoal-50 uppercase not-italic" data-id="element-453" data-mp="1fqzjhgpbp5lh">
              — {block.author}
            </cite>}
        </blockquote>;
    default:
      return null;
  }
}
export function BlogPost() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const post = blogPosts.find(item => item.slug === slug);
  const category = post ? categories.find(item => item.id === post.category) : undefined;
  const body = post ? plainText(post.content) : '';

  /* Hooks must run unconditionally — the redirect happens after this call. */
  useSeo({
    title: post?.title ?? 'Journal',
    description: post?.excerpt ?? '',
    path: post ? `/journal/${post.slug}` : '/journal',
    image: post?.image,
    type: 'article',
    noIndex: !post,
    breadcrumbs: post ? [{
      name: 'Journal',
      path: '/journal'
    }, {
      name: post.title,
      path: `/journal/${post.slug}`
    }] : [{
      name: 'Journal',
      path: '/journal'
    }],
    schema: post ? [articleSchema({
      title: post.title,
      description: post.excerpt,
      path: `/journal/${post.slug}`,
      image: post.image,
      datePublished: post.date,
      section: category?.name,
      body,
      wordCount: body.split(/\s+/).filter(Boolean).length
    })] : undefined
  });
  if (!post || !category) {
    return <Navigate to="/journal" replace data-id="element-454" data-mp="10h00an7auzgq" />;
  }
  const Icon = category.icon;
  const sameCategory = blogPosts.filter(item => item.slug !== post.slug && item.category === post.category);
  const others = blogPosts.filter(item => item.slug !== post.slug && item.category !== post.category);
  const related: BlogPostType[] = [...sameCategory, ...others].slice(0, 3);
  return <div className="flex min-h-screen flex-col bg-cream" data-id="element-455" data-mp="1o482uxdcfuf2">
      <Header data-id="element-456" data-mp="1j8srcc2t6alt" />

      <main data-id="element-457" data-mp="1j8alcnqcgi0p">
        {/* BLOCK: article_hero */}
        <section className="relative flex min-h-[520px] items-center justify-center overflow-hidden" data-id="element-458" data-mp="3pxfi66syic0y">
          <img src={post.image} alt="" aria-hidden="true" loading="eager" fetchPriority="high" decoding="async" className="absolute inset-0 h-full w-full object-cover" data-id="element-459" data-mp="3kze27uqtustp" />
          <div aria-hidden="true" className="absolute inset-0 bg-charcoal/45" data-id="element-460" data-mp="25hr3u62ywnds" />

          <div className="relative z-10 mx-auto mt-16 max-w-4xl px-6 pb-24 text-center" data-id="element-461" data-mp="1ro9o6wdejut1">
            <span className="mb-5 inline-flex items-center gap-2 text-[11px] font-medium tracking-[0.25em] text-white/90 uppercase [text-shadow:0_1px_4px_rgba(0,0,0,0.3)]" data-id="element-462" data-mp="1vok3izn33xkk">
              <Icon size={14} strokeWidth={1.5} aria-hidden="true" data-id="element-463" data-mp="1mqggfywgasqn" />
              {category.name}
            </span>
            <h1 className="mb-4 font-serif text-4xl leading-tight text-white drop-shadow-md md:text-6xl" data-id="element-464" data-mp="18r8ewzctf8ja">
              {post.title}
            </h1>
            <time dateTime={post.date} className="font-light tracking-wide text-white/90 drop-shadow-md" data-id="element-465" data-mp="333y8th36ohqo">
              {formatDate(post.date)}
            </time>
          </div>

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20" data-id="element-466" data-mp="jx2vy57ondck">
            <WavyDivider fill={SURFACE.cream} data-id="element-467" data-mp="exzo6d0bit0w" />
          </div>
        </section>

        {/* BLOCK: article_body */}
        <article className="bg-cream px-6 py-20" data-id="element-468" data-mp="avcus7o0101l">
          <div className="mx-auto max-w-2xl space-y-6" data-id="element-469" data-mp="ay8t3x3rdqqk">
            {post.content.map((block, index) => <ContentRenderer key={`${block.type}-${index}`} block={block} data-id="element-470" data-mp="5zecn1t9ke9m" data-mpi={index} />)}

            <div className="mt-8 border-t border-cream-200 pt-12" data-id="element-471" data-mp="12pezgva2okoa">
              <Link to="/journal" className="group inline-flex items-center text-sm tracking-widest text-sage-700 uppercase transition-colors hover:text-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4" data-id="element-472" data-mp="yn0h0cabfmxj">
                <ArrowLeft size={16} strokeWidth={1.5} className="mr-2 transition-transform group-hover:-translate-x-1" aria-hidden="true" data-id="element-473" data-mp="1mccvnroewn78" />
                Back to the journal
              </Link>
            </div>
          </div>
        </article>

        {/* BLOCK: related_posts */}
        {related.length > 0 && <section className="relative mt-12 bg-rose-50 px-6 pb-20 pt-32" aria-labelledby="continue-reading" data-id="element-474" data-mp="1yt45emwvdsso">
            <div className="pointer-events-none absolute inset-x-0 top-0 -translate-y-full" data-id="element-475" data-mp="22t602c46tlyg">
              <WavyDivider fill={SURFACE.rose} position="top" data-id="element-476" data-mp="27sr7laxfb2ms" />
            </div>

            <div className="mx-auto max-w-7xl" data-id="element-477" data-mp="2cru58ab2q6kq">
              <h2 id="continue-reading" className="mb-14 text-center font-serif text-3xl text-charcoal md:text-4xl" data-id="element-478" data-mp="2hjvd7k17lb4y">
                Continue reading
              </h2>
              <div className="mb-12 grid grid-cols-1 gap-8 md:grid-cols-3" data-id="element-479" data-mp="2qm4ls4cx2ufp">
                {related.map(item => <BlogCard key={item.slug} post={item} data-id="element-480" data-mp="2vkpkfzcit621" />)}
              </div>
              <div className="text-center" data-id="element-481" data-mp="3gtjrkreyjt6x">
                <Link to="/journal" className="group inline-flex items-center text-sm tracking-widest text-sage-700 uppercase transition-colors hover:text-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-700 focus:ring-offset-4" data-id="element-482" data-mp="3bw1rlqix4ob2">
                  Visit the journal
                  <ArrowRight size={16} strokeWidth={1.5} className="ml-2 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-483" data-mp="2y3u0aeaelpjr" />
                </Link>
              </div>
            </div>
          </section>}

        <SectionDivider from={SURFACE.rose} to={SURFACE.charcoal} data-id="element-484" data-mp="33259qnwsdy1g" />
      </main>

      <Footer data-id="element-485" data-mp="2fd3x0ogx80qd" />
    </div>;
}